/**
 * @file        src/api/hotels.js
 * @description Hotel API client + response adapters. Wraps the backend's
 *              TripJack v3 hotel flow (all routes are mounted under /api/v1
 *              but call the supplier's hms/v3 + oms/v3 endpoints internally):
 *                search → searchlist → detail → review. The booking itself is
 *                placed server-side after payment (/hotel-payment/*).
 *              Adapters reshape the v3 payloads into the plain objects the
 *              existing static views already consume (h.name, h.price, room
 *              cards with .rates etc.) so the UI markup stays untouched.
 * @author      Kinetika Labs
 * @created     2026-06-23
 * @changelog
 *   - 2026-11-03 Claude — flightAmendmentPreview takes an optional `extra`
 *                          ({ selection, counts }) for the Void page: a VOID may name
 *                          SOME passengers on SOME flights and is priced on exactly
 *                          those. Absent, the preview asks about the whole booking.
 *   - 2026-10-27 Claude — flightAmendmentCheckStatus() + flightCancelAbort(), for
 *                          the Cart Amendments card's Check Status + Abort buttons.
 *   - 2026-10-25 Claude — Removed book() and bookingInfo(): nothing called them, and
 *                          their routes (/hotel-book, /hotel-booking-info) were
 *                          deleted from the backend as unauthenticated (SECURITY).
 *   - 2026-10-15 Claude — flightCancelPreview/Submit take an optional `extra`
 *                          ({ selection, counts }) for the Cancellation page (§43).
 *   - 2026-10-13 Claude — flightAmendmentSubmit takes an optional `extra` ({ reasons, selection }) for the Full Refund page.
 *   - 2026-10-07 Claude — getHotelConvenienceFee takes the stay ({ optionId, rooms,
 *                          nights }): a flat hotel convenience fee is per room,
 *                          per night, like the markup.
 *   - 2026-10-06 Claude — getHotelConvenienceFee / fetchHotelOffers /
 *                          checkHotelCoupon take the `hotelId`, so the server can
 *                          judge a Domestic / International rule (admin migration
 *                          076) on the property. checkHotelCoupon now returns
 *                          { row, reason } like checkFlightCoupon, so a refused
 *                          code can say why.
 *   - 2026-09-28 Claude — HOLDBOOK: cancelHotelHold() (POST /hotel-hold/:ref/cancel).
 *   - 2026-09-11 Claude — HOLDBOOK: createHotelHold() (POST /hotel-hold) and
 *                          getHotelHold() (GET /hotel-hold/:ref). Confirming a hold
 *                          reuses initiateHotelPayment() with `{ holdRef }`.
 *   - 2026-08-07 Claude — descriptionSections() drops the supplier's "Default"
 *                          description key (client request). It is a payload slot
 *                          name rather than a customer-facing heading, and its text
 *                          is routinely the same blurb Overview already carries, so
 *                          the Property Description showed a third identical block
 *                          titled "Default". Attractions and every other section are
 *                          unchanged, and sections are still NOT de-duplicated
 *                          against each other — only this key is removed.
 *   - 2026-06-23 Claude — Initial. Connects the static hotel pages to the
 *                          live v3 backend (booking_backend :5000).
 *   - 2026-07-07 Claude — groupRooms(): options of the SAME room merge into one
 *                          card with a cheapest-first rate row per option; meal
 *                          basis leads each rate's perks (Room Only/Breakfast).
 */
import { post, get, put, del } from './client.js';
import { NO_IMAGE } from '../lib/placeholder.js';

const FALLBACK_IMG = NO_IMAGE;

// Hotel endpoints are served under /api/v3 (they call TripJack hms/v3 + oms/v3).
// Only the hotel calls use this base; auth/other APIs keep the default /api/v1.
const HOTEL_BASE = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/v1$/, '/v3');
const V3 = { baseURL: HOTEL_BASE };

// ---- low-level endpoint calls ---------------------------------------------

/** POST /hotel-search — city/hotel autocomplete. Returns { searchQuery, total, results }. */
export async function autocomplete(hotelname) {
  return post('/hotel-search', { hotelname }, V3);
}

/** POST /hotel-searchlist — listing for a city + dates + occupancy. */
export async function searchList(searchQuery, correlationId) {
  return post('/hotel-searchlist', { searchQuery, correlationId }, V3);
}

/** POST /hotelDetail-search — full property + priced room options. */
export async function detailSearch(body) {
  return post('/hotelDetail-search', body, V3);
}

/** POST /hotel-review — re-price + lock a chosen option, returns a bookingId. */
export async function review(body) {
  return post('/hotel-review', body, V3);
}

/** POST /create-hotel-booking — persist the booking row locally (needs auth). */
export async function createLocalBooking(body) {
  return post('/create-hotel-booking', body, V3);
}

/**
 * POST /hotel-payment/initiate — start an Easebuzz hosted-checkout payment.
 * Returns { success, txnid, paymentUrl }. The caller then redirects the browser
 * to `paymentUrl`; Easebuzz posts the result back to the backend, which books
 * the hotel and redirects to /confirmation.
 */
export async function initiateHotelPayment(body) {
  return post('/hotel-payment/initiate', body, V3);
}

/**
 * The total-changed 409 from /hotel-payment/initiate, or null.
 *
 * The server refuses to charge a grand total the guest has not seen: its own
 * figure (re-reviewed room price + convenience fee - auto discount - coupon) must
 * agree with the one on the checkout summary to within Rs.1. Before this existed
 * the hotel flow had NO total guard at all — the case that surfaced it was
 * /hotel-offers and /hotel-convenience-fee having no optionalAuth, so a corporate
 * guest was shown RETAIL's fee/discount/coupon and charged the B2B figures.
 *
 * Continuing re-posts with `acceptChanges`, which waives the stop and never the
 * pricing. `couponApplied` is null when the applied code did not survive
 * re-validation for THIS buyer, so the page can clear it.
 *   { newTotal, previousTotal, breakdown, couponApplied, message }
 */
export function hotelTotalChangeInfo(e) {
  const body = e?.response?.data || e?.body || e;
  if (body?.code !== 'TOTAL_CHANGED') return null;
  return {
    newTotal: Number(body.newTotal) || 0,
    previousTotal: Number(body.previousTotal) || 0,
    breakdown: body.breakdown || null,
    couponApplied: body.couponApplied || null,
    message: body.message || '',
  };
}

/**
 * Zero-gateway settle: when the wallet covers the whole total, initiate returns
 * { walletSettle:true, txnid }; this places the booking server-side (capturing the
 * wallet hold) and returns { redirect } to navigate to. Authenticated.
 */
export async function hotelWalletSettle(body) {
  return post('/hotel-payment/wallet-settle', body, V3);
}

/**
 * HOLDBOOK — POST /hotel-hold: hold the room WITHOUT payment (TripJack book with
 * no paymentInfos → ON_HOLD). The server re-reviews the rate itself. Login
 * required. Returns { success, hold } — see getHotelHold for the hold shape.
 */
export async function createHotelHold(body) {
  return post('/hotel-hold', body, V3);
}

/**
 * HOLDBOOK — GET /hotel-hold/:ref: the customer's own hold, read live from the
 * supplier — { ref, status, holdDeadline, canConfirm, expired, message, quote,
 * hotelName, roomName, checkIn, checkOut, guests, rooms, leadGuest }.
 */
export async function getHotelHold(ref) {
  return get(`/hotel-hold/${encodeURIComponent(ref)}`, V3);
}

/**
 * HOLDBOOK — POST /hotel-hold/:ref/cancel: release the customer's own unpaid hold
 * at TripJack. Returns { success, hold }. Refused (409) while a Confirm & Pay for
 * the hold is still in progress. Nothing was charged, so nothing is refunded.
 */
export async function cancelHotelHold(ref) {
  return post(`/hotel-hold/${encodeURIComponent(ref)}/cancel`, {}, V3);
}

/** GET /hotel-bookings/mine — the logged-in user's hotel bookings. */
export async function myHotelBookings() {
  return get('/hotel-bookings/mine', V3);
}

/** GET /flight-bookings/mine — the logged-in user's flight bookings. */
export async function myFlightBookings() {
  return get('/flight-bookings/mine', V3);
}

/**
 * GET /flight-booking/:ref — full data for ONE flight booking (the printable
 * voucher). Returns the real paid total + live itinerary legs/passengers.
 */
// LOGIN REQUIRED (2026-07-20). The backend route is behind authenticateToken and
// scopes the lookup to the caller's user_id, so the access token is the only proof
// of ownership. The second arg is accepted and IGNORED: call sites still pass the
// URL's ?email=, and forwarding it would put a customer's email into request logs
// and Referer headers for no benefit — the server no longer reads it. A 401 is
// handled by the client interceptor (refresh, then force-logout to the login page).
export async function flightBookingByRef(ref, _email) {
  return get(`/flight-booking/${encodeURIComponent(ref)}`, V3);
}

/**
 * GET /hotel-booking/:ref — full data for ONE hotel booking (the Booking
 * Details page + hotel invoice): local amounts, stay, live rooms/travellers,
 * cancellation policy and the payment trail.
 */
// See flightBookingByRef: login required, second arg accepted and ignored.
export async function hotelBookingByRef(ref, _email) {
  return get(`/hotel-booking/${encodeURIComponent(ref)}`, V3);
}

/**
 * Flight cancellation (token-less — booking ref + booking email is the
 * capability). preview returns { cancellable, refundAmount, penalty, ... };
 * submit commits it; status returns the refund/cancellation state.
 */
// `extra` — the Cancellation page's { selection: [{ journey, pax: [i…] }], counts:
// { journeys, travellers } }. A selection names the passengers and flights to
// cancel; the counts are this page's own view of the booking, which the server
// checks against the airline's before acting on a list of POSITIONS. Absent (the
// hotel modal, the admin console's proxy), the whole booking is cancelled —
// unchanged behaviour.
export async function flightCancelPreview(ref, email, extra = {}) {
  return post('/flight-cancel/preview', { ref, email, ...extra }, V3);
}
export async function flightCancelSubmit(ref, email, reason, extra = {}) {
  return post('/flight-cancel/submit', { ref, email, reason, ...extra }, V3);
}
export async function flightCancelStatus(ref, email) {
  return post('/flight-cancel/status', { ref, email }, V3);
}
// Ask TripJack right now about one still-open amendment (Cart Amendments
// card's "Check Status" button), instead of waiting on the background refresh
// that already runs on every page load of an unsettled booking.
export async function flightAmendmentCheckStatus(ref, email, amendmentId) {
  return post('/flight-amendment/check-status', { ref, email, amendmentId }, V3);
}
// Abort a still-open cancellation/void/full-refund request. Same ref+email
// capability as the rest of /flight-cancel/*; only succeeds once TripJack
// confirms, otherwise the request is recorded for the team to action.
export async function flightCancelAbort(ref, email, amendmentId, remarks = '') {
  return post('/flight-cancel/abort', { ref, email, amendmentId, remarks }, V3);
}

/**
 * Flight amendment actions beyond a plain cancellation (agent portal). Same
 * ref+email capability. Void / Full Refund are preview→submit (supplier charges);
 * the email request types (Fare Change, Correction, …) are recorded for ops.
 */
// `extra` — the Void page's { selection: [{ journey, pax: [i…] }], counts }.
// A VOID may name SOME passengers on SOME flights (client 2026-11-03), and the
// quote is then struck on exactly those: the server resolves each POSITION to
// the traveller the airline holds there and refuses rather than widening.
// Absent, the preview asks about the whole booking, exactly as before.
export async function flightAmendmentPreview(ref, email, type, extra = {}) {
  return post('/flight-amendment/preview', { ref, email, type, ...extra }, V3);
}
// `extra` — the Full Refund page's { reasons: [key…], selection: [{ journey, pax: [i…] }] }
// or the Void page's { selection, counts }. The server validates both; absent,
// the old whole-booking submit is unchanged.
export async function flightAmendmentSubmit(ref, email, type, reason, extra = {}) {
  return post('/flight-amendment/submit', { ref, email, type, reason, ...extra }, V3);
}
export async function flightAmendmentRequest(ref, email, type, remarks) {
  return post('/flight-amendment/request', { ref, email, type, remarks }, V3);
}
// Absolute URL to the credit-note document (opened in a new browser tab, so it
// must be a full path the browser can navigate to, not an axios call). Proxied
// from TripJack's dashboard by the backend; capability = booking email.
export function flightCreditNoteUrl(amendmentId, email) {
  const base = HOTEL_BASE.replace(/\/$/, '');
  return `${base}/flight-amendment/credit-note/${encodeURIComponent(amendmentId)}?email=${encodeURIComponent(email || '')}`;
}

/** Hotel cancellation — same ref + email capability. */
export async function hotelCancelPreview(ref, email) {
  return post('/hotel-cancel/preview', { ref, email }, V3);
}
export async function hotelCancelSubmit(ref, email, reason) {
  return post('/hotel-cancel/submit', { ref, email, reason }, V3);
}
export async function hotelCancelStatus(ref, email) {
  return post('/hotel-cancel/status', { ref, email }, V3);
}
// Abort a REQUESTED cancellation — undo a pending cancellation and reactivate the
// booking (only succeeds once the supplier confirms; otherwise it errors).
export async function hotelCancelAbort(ref, email, remarks = '') {
  return post('/hotel-cancel/abort', { ref, email, remarks }, V3);
}

/**
 * Verify a PAN against the name on it, via Protean eGov's Online PAN
 * Verification (the backend calls it; no credential of ours reaches the browser).
 *
 * Returns { available, verified, name, message } plus, from Protean,
 * `advisory` — set when the PAN is valid but its record carries something worth
 * showing (inoperative / merged / deceased / name mismatch). `advisory` is never
 * a rejection.
 *
 * `available:false` → the verification service is unreachable, unconfigured, or
 * out of prepaid balance; the caller accepts a format-valid PAN and must not
 * block the booking on it.
 *
 * NOTE: `name` in the RESPONSE is always null from Protean, which returns only a
 * Y/N match flag rather than echoing the registered name.
 */
export async function verifyPan(pan, name) {
  return post('/verify-pan', { pan, name }, V3);
}

// `&hotelId=…` when the hotel is known: the server judges Domestic /
// International rules on the property's country, exactly as initiate does.
const hotelParam = (hotelId) => (hotelId ? `hotelId=${encodeURIComponent(hotelId)}` : '');

/**
 * The admin convenience fee that applies to a hotel booking of `amount` (the
 * pre-convenience customer total) at `hotelId`. Returns { amount, name }; amount
 * 0 when none. Without a hotelId only an unscoped (Domestic + International) fee
 * can apply.
 */
export async function getHotelConvenienceFee(amount, hotelId, stay = {}) {
  const h = hotelParam(hotelId);
  // A flat fee is PER ROOM, PER NIGHT. The server reads the stay off the option
  // it priced (optionId); rooms / nights are only its fallback.
  const extra = ['optionId', 'rooms', 'nights']
    .filter((k) => stay?.[k] != null && stay[k] !== '')
    .map((k) => `&${k}=${encodeURIComponent(stay[k])}`)
    .join('');
  return get(`/hotel-convenience-fee?amount=${encodeURIComponent(Math.round(Number(amount) || 0))}${h ? `&${h}` : ''}${extra}`, V3);
}

/**
 * GET /hotel-offers — the hotel checkout's "Offers & Promo Code" panel data: the
 * active convenience fee, any auto hotel-discount rules and the visible coupon
 * codes (product 'hotel'), narrowed server-side to the ones whose Domestic /
 * International scope fits `hotelId`. Display-only (the money charged is
 * recomputed server-side at initiate). Never throws — returns empties so the
 * panel degrades.
 */
export async function fetchHotelOffers(hotelId) {
  try {
    const h = hotelParam(hotelId);
    const res = await get(`/hotel-offers${h ? `?${h}` : ''}`, V3);
    return {
      convenienceFee: res?.convenienceFee || null,
      hotelDiscounts: Array.isArray(res?.hotelDiscounts) ? res.hotelDiscounts : [],
      coupons: Array.isArray(res?.coupons) ? res.coupons : [],
    };
  } catch (_) {
    return { convenienceFee: null, hotelDiscounts: [], coupons: [] };
  }
}

/**
 * GET /check-coupon/:code?product=hotel — validate a promo code the customer
 * typed (supports codes not in the visible list). Requires a logged-in session.
 * Returns { row, reason } — the coupon row { code, discount_type, discount_value }
 * on success, else null with the server's `reason` ('invalid' | 'expired' |
 * 'exhausted' | 'scope' | 'domestic_only' | 'international_only') or 'network'.
 * `hotelId` lets the server refuse a code scoped to the other half (Domestic vs
 * International) with that reason. Uses the default /api/v1 base (the coupon
 * endpoint is shared with flights). Never throws.
 */
export async function checkHotelCoupon(code, hotelId) {
  const c = String(code || '').trim();
  if (!c) return { row: null, reason: null };
  try {
    const h = hotelParam(hotelId);
    const res = await get(`/check-coupon/${encodeURIComponent(c)}?product=hotel${h ? `&${h}` : ''}`);
    const row = res?.success && Array.isArray(res.data) ? res.data[0] : null;
    return { row: row || null, reason: row ? null : (res?.reason || 'invalid') };
  } catch (_) {
    return { row: null, reason: 'network' };
  }
}

/** GET /me/profile — the logged-in user's editable profile. */
export async function getMyProfile() {
  return get('/me/profile', V3);
}

/** PUT /me/profile — update name / mobile / home airport. */
export async function updateMyProfile(body) {
  return put('/me/profile', body, V3);
}

// ---- favourites (all require a Bearer token) -------------------------------

/** POST /hotel-favourite — { hotelId, hotelName, cityCode, cityName }. */
export async function addFavourite(body) {
  return post('/hotel-favourite', body, V3);
}

/** DELETE /hotel-favourite/:hotelId. */
export async function removeFavourite(hotelId) {
  return del(`/hotel-favourite/${encodeURIComponent(hotelId)}`, V3);
}

/** GET /hotel-favourite/check/:hotelId → { isFavourite }. */
export async function checkFavourite(hotelId) {
  return get(`/hotel-favourite/check/${encodeURIComponent(hotelId)}`, V3);
}

/**
 * GET /hotel-favourites?city_code=<code> → the user's saved hotels for a city.
 * Returns a plain array of hotelIds (backend rows are { hotel_id }).
 */
export async function listFavourites(cityCode) {
  const res = await get(`/hotel-favourites?city_code=${encodeURIComponent(cityCode)}`, V3);
  const rows = res?.data || res || [];
  return rows.map((r) => String(r.hotel_id ?? r.hotelId ?? r)).filter(Boolean);
}

// ---- helpers ---------------------------------------------------------------

/** Pull a numeric price out of the v3 pricing block (FP is the marked-up final price). */
export function optionPrice(opt) {
  const p = opt?.pricing || {};
  return Math.round(Number(p.FP ?? p.totalPrice ?? 0)) || 0;
}

function ratingWord(score) {
  if (score >= 9) return 'Amazing';
  if (score >= 8) return 'Very good';
  if (score >= 7) return 'Good';
  if (score >= 5) return 'Pleasant';
  return 'Rated';
}

// Return ONLY the property's / room's real supplier images (deduped). No stock
// fallback — callers render a neutral placeholder when the list is empty so we
// never show photos that don't belong to the property.
//
// The supplier's FIRST image is frequently a generic / shared shot (often the
// same across many properties), so we promote the SECOND image to the front
// (and push the first to the end) when there are 2+ photos. This makes the card
// thumbnail, detail hero and room gallery all lead with a more representative,
// property-specific photo. A single-image list is left untouched.
function imagesOf(node, fallbackList) {
  const urls = (node?.images || [])
    .map((im) => (typeof im === 'string' ? im : im?.url))
    .filter(Boolean);
  if (urls.length > 1) return [...urls.slice(1), urls[0]];
  if (urls.length) return urls;
  return fallbackList && fallbackList.length ? fallbackList : [];
}

// Build a clean, de-duplicated address line from the enriched address object
// { line, city, state, country, postalCode, latitude, longitude }. The supplier
// `line` often already contains the city/state/country/postcode, so naively
// appending them produces "…, Mumbai, Maharashtra, India, 400052, Mumbai,
// Maharashtra, India". We split everything into comma tokens and dedupe
// (case-insensitive) so each part appears once.
function areaOf(addr = {}) {
  const parts = [];
  const push = (v) => {
    if (v == null) return;
    String(v).split(',').forEach((s) => {
      const t = s.trim();
      if (t) parts.push(t);
    });
  };
  push(addr.line);
  push(addr.city);
  push(addr.state);
  push(addr.country);
  push(addr.postalCode);
  const seen = new Set();
  return parts
    .filter((p) => {
      const k = p.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .join(', ');
}

// Facilities come back as an array of strings or { name }/{ title } objects.
// The supplier list often repeats entries (e.g. "Laundry Facilities" or "Wifi"
// twice), so de-duplicate case-insensitively (trimmed, collapsed whitespace),
// keeping the first-seen original casing. This one fix cleans the icon strip,
// the Amenities grid AND the Services & Amenities list (all read this).
export function normalizeFacilities(facilities) {
  if (!Array.isArray(facilities)) return [];
  const seen = new Set();
  return facilities
    .map((f) => (typeof f === 'string' ? f : f?.name || f?.title || f?.label))
    .filter(Boolean)
    .map((f) => String(f).replace(/\s+/g, ' ').trim())
    .filter((f) => {
      const k = f.toLowerCase();
      if (!f || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
}

// Description can be a plain string or a JSON-stringified { section: text } map.
export function normalizeDescription(desc) {
  if (!desc) return '';
  if (typeof desc !== 'string') return String(desc);
  const trimmed = desc.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const obj = JSON.parse(trimmed);
      if (Array.isArray(obj)) return obj.filter(Boolean).join('\n\n');
      if (obj && typeof obj === 'object') {
        return Object.values(obj).filter((v) => typeof v === 'string' && v.trim()).join('\n\n');
      }
    } catch { /* fall through to raw */ }
  }
  return desc;
}

// Turn a JSON-stringified { section: text } description into titled sections
// for an attractive, structured render. Returns null when the description is
// plain prose (so the caller falls back to paragraph rendering).
export function descriptionSections(desc) {
  if (!desc || typeof desc !== 'string') return null;
  const trimmed = desc.trim();
  if (!trimmed.startsWith('{')) return null;
  let obj;
  try { obj = JSON.parse(trimmed); } catch { return null; }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;

  const titleize = (k) =>
    k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const sections = Object.entries(obj)
    .filter(([, v]) => typeof v === 'string' && v.trim())
    .map(([k, v]) => {
      const text = v.trim();
      // A "distances/attractions" blob looks like "Name – 0.4 km / 0.2 mi …".
      // Split it into individual place entries for a clean list.
      const isDistances = /km\s*\/\s*[\d.]+\s*mi/i.test(text);
      let items = null;
      if (isDistances) {
        items = text
          .split(/(?<=mi)\s+(?=[A-Z])/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (items.length < 2) items = null;
      }
      return { title: titleize(k), text, items };
    })
    // Drop the supplier's "Default" bucket. It is a payload slot name, not a
    // heading a customer should read, and its text is routinely the same blurb
    // already carried by Overview — so it rendered as a third identical block.
    // Only this one key is dropped: Overview / Attractions / everything else are
    // untouched, and no de-duplication is done between the sections that remain.
    // If "Default" was the ONLY key, `sections` ends up empty and we return null,
    // which makes the detail page fall back to rendering the description as plain
    // prose — so the text is still shown, just without the bogus heading.
    .filter((s) => s.title.toLowerCase() !== 'default');

  return sections.length ? sections : null;
}

// ---- adapters: v3 → UI shapes ---------------------------------------------

/**
 * Map one listing hotel to the card shape used by HotelResults / the store.
 * Keeps the raw payload on `_raw` so the detail step can reuse ids/options.
 */
export function toCard(hotel) {
  const options = hotel.options || [];
  const prices = options.map(optionPrice).filter((n) => n > 0);
  const price = prices.length ? Math.min(...prices) : 0;
  const stars = Number(hotel.starRating) || 0;
  // Live filterable attributes derived from the options on the listing payload.
  const meals = Array.from(
    new Set(options.map((o) => (o.mealBasis || '').trim()).filter(Boolean)),
  );
  const refundable = options.some((o) => o?.cancellation?.isRefundable === true);
  // Real guest rating ONLY — never fabricate one from the star count.
  const rating = hotel.rating ?? null;
  const gallery = imagesOf(hotel, null);
  const addr = hotel.address || {};
  const about = normalizeDescription(hotel.description);

  return {
    id: hotel.hotelId || hotel.tjHotelId || hotel.id,
    name: hotel.name || 'Hotel',
    area: areaOf(addr),
    stars,
    rating,
    word: rating ? ratingWord(Number(rating)) : '',
    reviews: hotel.reviews ?? 0,
    price,
    gross: price,
    discount: undefined,
    room: options[0]?.roomName || '',
    snippet: about ? about.replace(/\s+/g, ' ').slice(0, 90) : '',
    neonBest: false,
    tags: [],
    meals,
    refundable,
    img: gallery[0] || FALLBACK_IMG,
    gallery,
    about,
    aboutSections: descriptionSections(hotel.description),
    // content fields used by the detail sections
    address: addr,
    facilities: normalizeFacilities(hotel.facilities),
    propertyType: hotel.propertyType || 'HOTEL',
    latitude: addr.latitude ?? null,
    longitude: addr.longitude ?? null,
    _raw: hotel,
  };
}

/** Map a detail-search hotel into the store hotel object (card shape + options). */
export function toDetailHotel(hotel) {
  const card = toCard(hotel);
  card.checkInTime = hotel.checkInTime?.beginTime || null;
  card.checkOutTime = hotel.checkOutTime?.beginTime || null;
  card.options = hotel.options || [];
  // Refundability summary from the cheapest/first option for the Policies block.
  const firstCancel = card.options.find((o) => o.cancellation)?.cancellation || null;
  card.refundable = firstCancel ? Boolean(firstCancel.isRefundable) : null;
  return card;
}

/**
 * Map a v3 room option to the RoomCard shape (one option = one room w/ one rate).
 * Carries optionId on the rate so reserve() can chain into review/book.
 */
export function toRoom(opt, hotelGallery) {
  const info = (opt.roomInfo && opt.roomInfo[0]) || {};
  // Per-room images (roomInfo[].images) when present, else the hotel gallery.
  const gallery = imagesOf(info, hotelGallery);
  const refundable = opt.cancellation?.isRefundable;
  // Meal basis leads the perks so grouped rate rows read like the reference —
  // "Breakfast · Non-refundable" vs "Room Only · Non-refundable".
  const meal = (opt.mealBasis || '').trim();
  const perks = [
    meal || 'Room Only',
    refundable ? 'Free cancellation' : 'Non-refundable',
    'Instant confirmation',
  ];
  // Room-specific amenities + bed config from the v3 static-detail enrichment.
  const amen = Array.isArray(info.amenities) ? info.amenities : [];
  return {
    name: opt.roomName || info.name || 'Room',
    img: gallery[0] || FALLBACK_IMG,
    gallery,                         // used by the room details modal
    sleeps: info.roomOccupancy || info.occupancy || 2,
    beds: info.bedConfig || '',
    size: '',
    amen,                            // room-level amenities (names)
    modal: [],
    pp: null,
    // filterable attributes (live) for the room-types filter bar
    mealBasis: (opt.mealBasis || '').trim(),
    refundable: opt.cancellation ? Boolean(opt.cancellation.isRefundable) : null,
    panRequired: Boolean(opt.compliance?.panRequired),
    rates: [
      {
        perks,
        price: optionPrice(opt),
        optionId: opt.optionId,
        _option: opt,
      },
    ],
  };
}

/**
 * Group per-option rooms (toRoom output) that are the SAME physical room
 * (name + bed config + occupancy) into ONE card with a rate row per option —
 * e.g. "Deluxe Double · Room Only ₹4,087" and "Deluxe Double · Breakfast
 * ₹4,358" under a single card instead of two near-identical cards. Rates are
 * sorted cheapest-first (RoomCard badges rate[0] as "Today's best price!").
 * Room-level filter fields become aggregates across the group's options.
 */
export function groupRooms(list) {
  const map = new Map();
  for (const r of list) {
    const key = [String(r.name).toLowerCase().trim(), r.beds || '', r.sleeps || ''].join('|');
    const g = map.get(key);
    if (!g) {
      map.set(key, {
        ...r,
        mealBases: r.mealBasis ? [r.mealBasis] : [],
        rates: [...r.rates],
      });
    } else {
      g.rates.push(...r.rates);
      if (r.mealBasis && !g.mealBases.includes(r.mealBasis)) g.mealBases.push(r.mealBasis);
      // Aggregates for the room-types filter bar: refundable if ANY option is,
      // PAN-optional if ANY option skips it.
      if (r.refundable === true) g.refundable = true;
      if (r.panRequired === false) g.panRequired = false;
      if ((!g.gallery || !g.gallery.length) && r.gallery?.length) { g.gallery = r.gallery; g.img = r.img; }
      g.amen = [...new Set([...(g.amen || []), ...(r.amen || [])])];
    }
  }
  const out = [...map.values()];
  for (const g of out) g.rates.sort((a, b) => (a.price || 0) - (b.price || 0));
  return out;
}

/** Build the /hotel-searchlist `searchQuery` payload from store search params.
 *  `hidOffset`/`hidLimit` page the backend's supplier fetch — pass the next
 *  page's offset to fetch further hotels for a big city in the background. */
export function buildSearchQuery({ city, hids, checkIn, checkOut, rooms, currency = 'INR', nationality = '106', hidOffset, hidLimit }) {
  const sq = {
    searchCriteria: { city, currency, nationality },
    checkinDate: checkIn,
    checkoutDate: checkOut,
    roomInfo: (rooms && rooms.length ? rooms : [{ numberOfAdults: 2, numberOfChild: 0 }]).map((r) => ({
      numberOfAdults: r.numberOfAdults ?? r.adults ?? 2,
      numberOfChild: r.numberOfChild ?? r.children ?? 0,
      ...(r.childAge && r.childAge.length ? { childAge: r.childAge } : {}),
    })),
  };
  const prefs = {};
  if (hids && hids.length) prefs.hids = hids;
  if (hidOffset != null) prefs.hidOffset = hidOffset;
  if (hidLimit != null) prefs.hidLimit = hidLimit;
  if (Object.keys(prefs).length) sq.searchPreferences = prefs;
  return sq;
}
